// The three racing strategies, and the one of them that prints its own tickets.
//
// Everything a race needs the engine to do, the engine already does: a race is
// a match whose `selections` are its runners (`src/lib/racing/board.js`), so
// `runStrategy` shops it, prices it, screens it and prints it exactly as it
// does a football board. Two of the three below are therefore nothing but
// documents — data, no code — and that is the point of the boundary rule.
//
// THE THIRD IS NOT, and the reason is worth writing down. An all-up is not a
// value bet and has no model to rank legs with: it takes the shortest
// favourites on the card and the only dial is HOW MANY of them to stack. The
// engine's ticket loop ranks candidates by edge and hands the leg rule the best
// of them, which for a no-model strategy is a hash of the ids — the right
// answer for a shape like Favourites and Two Long Shots, and the wrong one
// here, where "the next three races" is the whole instruction. And three
// tickets that differ only in leg count is not something `combiner.tickets`
// can say: the loop takes each match once and would print 2, then 3 more, then
// 4 more, nine races deep, instead of the same card read three ways.
//
// So it gets its own printer, the way Classic does (`strategies/classic.js`),
// and `runStrategy` routes to it on the id. The tickets that come out are the
// same `{ multi, legs }` every other strategy prints — nothing downstream
// learns that a second printer exists.

import { RACING_SPORTS, isRacingSport } from '../teams.js';
// The one writer of a tier string in the app. A strategy says what to pick;
// what the result is called is read off its combined chance. See lib/tier.js.
import { tierFor } from '../../tier.js';
import { legLink } from '../../bookmakers.js';
import { bestPriceToTake, bookFair, priceAt, selectionsOf } from '../universe.js';
import { combineLegs } from '../combine.js';
// ¼ Kelly, 2% a ticket, 10% of the bankroll across the day (engine review #13).
// The two racing strategies that measure an edge stake by it; the all-up, which
// measures none, stays flat and draws nothing out of the day's budget.
import { KELLY_STAKING } from '../kelly.js';
// One description of a leg for both kinds of market — "Winx", "Randwick R5",
// and the saddlecloth that rides with the name. `strategy.js` imports this
// module for the dispatch below and this module imports that one back for
// `describe`: both uses are inside functions, so the cycle is resolved by the
// time either is called, and there is only ever one wording of a leg.
import { describe } from '../strategy.js';

const cents = (n) => Math.round(n * 100) / 100;

/**
 * ONE RUNNER A RACE, WHERE YOUR BOOK IS OVER THE EXCHANGE.
 *
 * The model is market consensus, and on a racing card the sharpest vote in that
 * consensus is the exchange — it is already in it, deflated by commission
 * (`bookFairNet`, via `outliers.js`'s `deflated`), so nothing here has to name
 * it. `distinctEvents` does the rest: the engine takes one leg per `matchKey`,
 * and a race IS a matchKey, so "one runner a race" is not a rule this document
 * has to carry.
 *
 * The price band is where an all-up stops being an all-up. Below $1.40 the edge
 * a book gives away is smaller than the commission on measuring it; past $8 the
 * fair price rests on the tail of a fourteen-runner devig, which is the part of
 * the vector every method disagrees about.
 */
export const RACING_VALUE = {
  id: 'racing-value',
  name: 'Racing value',
  version: 1,
  builtin: true,
  description: 'One runner a race, where your book pays more than the exchange says the runner is worth.',
  filters: {
    sports: [...RACING_SPORTS],
    markets: ['win'],
    priceBand: [1.4, 8],
    daysToKickoff: [0, 0],
    rules: [],
  },
  // SHIN, NOT POWER, AND ONLY ON A RACE (engine review #7). Power fits a
  // two-way market and spreads a book's margin evenly across the vector; a
  // fourteen-runner field does not carry it evenly — the favourite–longshot
  // bias loads it into the tail, which is exactly where a racing strategy is
  // deciding whether a $7 runner is value. Shin models that bias directly and
  // has been in `devig.js` since the first week. Both readings move: the
  // leg's own fair price (`activeLeg.devig`) and the market it is measured
  // against (`probabilitySource.params.devig`), because a leg screened one way
  // and priced the other is two yardsticks on one ticket.
  probabilitySource: { model: 'market-consensus', params: { devig: 'shin' } },
  activeLeg: { minEdge: 0.03, minEv: 0.02, devig: 'shin' },
  legRule: { type: 'whelan' },
  combiner: { minLegs: 1, maxLegs: 4, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  // One slip at one book — the profile's. A race the punter's own book is not
  // pricing falls back to the best price on the board and the ticket says so,
  // exactly as the leg picker does.
  bookmakerRule: 'single',
  slots: 1,
};

/**
 * THE LEG COUNT IS THE DESIGN; THE CHANCE IS THE RISK.
 *
 * What a reader is choosing between here is HOW MANY RACES TO STRING TOGETHER,
 * so the three tickets differ in exactly that and in nothing else. What each
 * one is then CALLED is not the design's to say: `tierFor(combined.p)` reads
 * the risk off the combined chance, the same function every other strategy's
 * tickets go through.
 *
 * This document used to write the tier itself — two legs was `low` because it
 * was the shortest ticket — and on a card of $2.80 favourites that printed
 * "Low risk · 1 in 19 chance" on one line: a label arguing with the number
 * underneath it, with nothing to tell the reader which of the two to trust.
 * Two of the three tickets now often print the same word, which is the honest
 * answer when two designs land in one band.
 *
 * `probabilitySource: none` is the honest declaration: the favourite is the
 * favourite because the market says so, so there is no opinion to measure an
 * edge against and the engine does not screen one (see `runStrategy`). What
 * the ticket costs in margin is printed on it as the expected return instead.
 */
export const RACING_ALL_UP = {
  id: 'racing-all-up',
  name: 'Racing all-up',
  version: 1,
  builtin: true,
  description: 'The shortest favourites on the card, strung together. Two legs, three or four.',
  filters: {
    sports: [...RACING_SPORTS],
    markets: ['win'],
    // THE BAND IS A SELECTION RULE AND NOTHING ELSE. It says which runners are
    // short enough to be called the favourite rather than the top pick in an
    // open race; it is NOT how the ticket's risk is decided, and tightening it
    // to make "Low risk" honest would have been fixing a label by changing the
    // product. The label is `tierFor(combinedProbability)` now (lib/tier.js),
    // so an all-up of three $2.90 favourites prints whatever its chance says
    // and the band can stay where a punter would put it.
    //
    // $3.00 because a fourteen-runner field genuinely has favourites there —
    // a third of the market, twice the next runner — and $1.20 is the floor,
    // under which a leg is paying for the privilege of being on the slip.
    priceBand: [1.2, 3],
    daysToKickoff: [0, 0],
    rules: [],
  },
  probabilitySource: { model: 'none', params: {} },
  // SHIN, SAID OUT LOUD, for the reason Racing value gives above and for one
  // more that is this strategy's own. It read 'multiplicative', which spreads a
  // race's whole round proportionally across the field — and this is the one
  // strategy in the app whose every leg is the SHORTEST price in a big field,
  // which is precisely where that method is worst. The live ticket: two $1.20
  // Albion Park favourites in a 137% harness book devigged to 0.61 a leg, so
  // the pad printed "Fair $2.71 · pays $1.44 (−46.8%)" and called a pair of
  // 4-on favourites "Medium risk · 1 in 3 chance". Shin reads the same runners
  // at about 0.79 and the ticket prints the book's real margin.
  //
  // There is no model here to screen with, so this one field decides three
  // printed things at once: the leg's fair price, the Fair/pays gap on the
  // ticket, and the tier — `tierFor(pFairCombined)` reads the product of these
  // very numbers. `defaultDevig` in universe.js would now answer 'shin' for a
  // race anyway; it is written here because a strategy document that leaves its
  // arithmetic to a default is a document that does not say what it does.
  activeLeg: { minEdge: 0, minEv: 0, devig: 'shin' },
  // The floor and the ceiling of the three tickets — two legs and four. The
  // per-ticket counts are `ALL_UP_DESIGNS`, which is this strategy's own design
  // table the way `TIERS` is Classic's.
  legRule: { type: 'fixed', legs: 4 },
  combiner: { minLegs: 2, maxLegs: 4, tickets: 3, distinctEvents: true, excludeSGM: true },
  staking: { type: 'flat' },
  bookmakerRule: 'single',
  slots: 3,
};

/**
 * The mispriced-runner singles: `MISPRICED_ODDS` with a card under it.
 *
 * Same shape, same reasons (see its own comment in catalogue.js), and one
 * number moved: `minBooks` is four rather than three, because a race carries
 * far more margin per runner than a two-way market and three voters on a
 * fourteen-runner field is a disagreement, not a market. It is the racing floor
 * the Matches highlight already flags on — `thresholdsFor` in outliers.js — so
 * a runner flagged on the Racing tab and a runner printed here are the same
 * finding rather than two tests that happen to agree most days.
 */
export const RACING_MISPRICED = {
  id: 'racing-mispriced',
  name: 'Mispriced runners',
  version: 1,
  builtin: true,
  description: 'One runner at a time, at the one book paying over the odds. Nothing about the form.',
  filters: {
    sports: [...RACING_SPORTS],
    markets: ['win'],
    priceBand: [1, 1000],
    daysToKickoff: [0, 0],
    rules: [],
  },
  // Shin, for the reason Racing value gives above (engine review #7).
  probabilitySource: { model: 'market-consensus', params: { devig: 'shin' } },
  // `minEdge` zero for the reason Mispriced odds gives: edge is in probability
  // points and a long price that is 5% over the odds is a fraction of a point,
  // so an edge floor here would flag a runner on the card and then print no
  // ticket for it. The racing floor lives in `minEv`, and it is unchanged under
  // Shin — 5% of the stake back is the same claim whichever way the round was
  // taken off, and the two methods land within a point of each other on
  // anything short enough for this strategy's band.
  activeLeg: { minEdge: 0, minEv: 0.05, devig: 'shin' },
  legRule: { type: 'singles', max: 3 },
  combiner: { minLegs: 1, maxLegs: 1, tickets: 1, distinctEvents: true, excludeSGM: true },
  staking: { ...KELLY_STAKING },
  bookmakerRule: 'best-price',
  minBooks: 4,
  slots: 3,
};

/** The three, in the order Today prints them. */
export const RACING_CATALOGUE = [RACING_VALUE, RACING_ALL_UP, RACING_MISPRICED];

/**
 * Which tickets the all-up prints, and how long each one is.
 *
 * THREE DESIGNS, NOT THREE TIERS, and that is the whole of the change. They
 * used to carry a tier apiece — two legs was `low` because it was the shortest
 * ticket — which is a label written by a leg count rather than by the chance
 * underneath it, and it printed "Low risk · 1 in 19 chance" on the same line.
 * A design says how many races to string together; what the result IS gets
 * decided by `tierFor(combinedProbability)` when the ticket is printed.
 *
 * Three of them, and no more: an all-up of five races is not a product anybody
 * asked for.
 */
export const ALL_UP_DESIGNS = [
  { legs: 2 },
  { legs: 3 },
  { legs: 4 },
];

/**
 * Does this strategy deal from the RACING CARD rather than from the sports
 * board? Read off the document, not off a list of ids, so a punter's fork of
 * Racing value deals from the card too — a fork that quietly ran over the
 * football board would print nothing and never say why.
 *
 * Every sport, not some: a strategy that names a racing code AND the AFL is one
 * this app has no second board to give it, and the sports board is the one it
 * has always had.
 */
export function isRacingStrategy(strategy) {
  const sports = strategy?.filters?.sports;
  return Array.isArray(sports) && sports.length > 0 && sports.every(isRacingSport);
}

/** What Today says when a racing strategy has no card to run on. */
export const NO_RACING_CARD = 'No racing card yet · open the Racing tab.';

/**
 * HOW LONG A RACE HAS TO BE AWAY BEFORE A TICKET MAY BE WRITTEN ON IT.
 *
 * The card is ordered by jump time and the next race on it is usually minutes
 * off, so a strategy that takes whatever it finds first writes tickets nobody
 * can place: printed, read, and gone by the time somebody has the book open.
 * The buffer at the other end (`RACING_BUFFER_MS`, lib/timeline.js) expires a
 * ticket three minutes out; this is the deal's own half of the same rule, and
 * it is bigger on purpose, because a ticket born two minutes inside its own
 * expiry is not a proposal, it is a receipt.
 *
 * Ten minutes: a race on a busy card, plus the time it takes to read the ticket
 * and get on. Races inside it are counted and said out loud rather than quietly
 * dropped — a card whose first six races are all inside ten minutes is a
 * finding about the afternoon, not a strategy that found nothing.
 */
export const RACING_LEAD_MS = 10 * 60_000;

/**
 * And how far ahead the all-up prefers to look: three hours.
 *
 * NOT A FILTER, A PREFERENCE, and the difference matters at eleven at night.
 * The shortest favourite on a card can be five hours away, and an all-up built
 * out of the day's two shortest prices wherever they fall is a ticket whose
 * first leg jumps at ten past three and whose second jumps at eight: it ties a
 * punter's afternoon up and it is a bet nobody asked for. Inside the window the
 * ranking is the price, exactly as it was; outside it the races are still
 * there, behind every race in the window, so a late card still prints.
 */
export const RACING_HORIZON_MS = 3 * 60 * 60_000;

/**
 * The favourite in one race, at the book the ticket is written at:
 * `{ selection, price, bookmaker, viaBestPrice }`, or null.
 *
 * SHORTEST PRICE, NOT LIKELIEST FAIR PROBABILITY, and on a racing card those
 * are the same runner every time — one book's vector is devigged by one factor,
 * so ordering by price and ordering by fair chance cannot disagree. Price is
 * what the punter reads off the screen, so price is what it says.
 *
 * A book that is not pricing this race at all falls back to the best price a
 * punter could actually walk in and take (never the exchange), marked, so the
 * ticket can say which legs are not at the shop it names.
 */
export function favouriteOf(match, { bookmaker = null } = {}) {
  let best = null;
  const take = (selection, price, book, viaBestPrice) => {
    if (!Number.isFinite(price) || price <= 1) return;
    if (best && (price > best.price || (price === best.price && selection >= best.selection))) return;
    best = { selection, price, bookmaker: book, viaBestPrice };
  };
  for (const selection of selectionsOf(match)) {
    if (!match.prices?.[selection]?.length) continue;
    const own = bookmaker ? priceAt(match, selection, bookmaker) : null;
    if (own !== null) take(selection, own, bookmaker, false);
  }
  if (best) return best;
  for (const selection of selectionsOf(match)) {
    const shop = bestPriceToTake(match, selection);
    if (shop) take(selection, shop.price, shop.bookmaker, true);
  }
  return best;
}

/** A decimal price inside a [lo, hi] band, both ends included. */
const inBand = (price, [lo, hi]) => price >= lo && price <= hi;

/**
 * The all-up's own printer: the card's shortest favourites, read three ways.
 *
 * Same signature as `runClassic` and the same contract — `{ tickets,
 * candidates, notes }` — so `runStrategy` can route to it and nothing that
 * reads the result has to know which printer ran.
 *
 * Deterministic with no seed at all, which is the one place this differs from
 * every other strategy: "the next three races" is not a choice a tie-break
 * could break differently. A second deal off the same card deals the same
 * all-up, and `appendDealt` drops it as the duplicate it is.
 */
export function runRacingAllUp(universe, ctx = {}, strategy = RACING_ALL_UP) {
  const s = strategy ?? RACING_ALL_UP;
  const sports = s.filters?.sports ?? [];
  const band = s.filters?.priceBand ?? RACING_ALL_UP.filters.priceBand;
  // A fork that has dropped the field falls back to the RACE's own default,
  // which is Shin (`defaultDevig`, universe.js) — never multiplicative, which
  // is what this line used to say and is the method a big field fits worst.
  const devigMethod = s.activeLeg?.devig ?? null;
  const forDate = ctx.forDate ?? universe?.forDate;
  const asOfMs = new Date(ctx.asOf ?? universe?.asOf ?? Date.now()).getTime();
  const notes = [];

  const races = [...(universe?.matches?.values?.() ?? [])]
    .filter((m) => !sports.length || sports.includes(m.sport))
    .sort((a, b) => String(a.kickoff).localeCompare(String(b.kickoff))
      || String(a.matchKey).localeCompare(String(b.matchKey)));

  const candidates = [];
  let outOfBand = 0;
  let tooSoon = 0;
  for (const match of races) {
    // BEFORE ANYTHING ELSE IS ASKED OF IT. Whether the favourite is short
    // enough is a question about a race somebody could still back; a race
    // jumping in four minutes is not one, whatever it is paying.
    const jumpsIn = Date.parse(match.kickoff ?? '') - asOfMs;
    if (!Number.isFinite(jumpsIn) || jumpsIn < RACING_LEAD_MS) {
      // Only the ones still to come are worth a sentence. A race that has been
      // and gone is not a race this deal skipped, it is one the day has spent,
      // and counting it would have the note growing all afternoon.
      if (jumpsIn >= 0) tooSoon += 1;
      continue;
    }
    const fav = favouriteOf(match, { bookmaker: ctx.bookmaker });
    if (!fav) continue;
    const pFair = bookFair(match, fav.selection, fav.bookmaker, devigMethod);
    if (pFair === null) continue;
    if (!inBand(fav.price, band)) { outOfBand += 1; continue; }
    const named = describe(match, fav.selection);
    candidates.push({
      // Inside the window this ticket wants its legs in — the first key the
      // ranking sorts on, so the day's shortest price five hours away cannot
      // pull a punter's afternoon out of shape. See `RACING_HORIZON_MS`.
      soon: jumpsIn <= RACING_HORIZON_MS,
      key: `${match.matchKey}:${fav.selection}`,
      matchKey: match.matchKey,
      providerEventId: match.providerEventId ?? null,
      sport: match.sport,
      competition: match.competition,
      sportTitle: match.sportTitle ?? match.competition,
      kickoff: match.kickoff,
      inplayAt: match.inplayAt ?? null,
      marketClosedAt: match.marketClosedAt ?? null,
      selection: fav.selection,
      team: named.team,
      match: named.match,
      number: named.number,
      bookmaker: fav.bookmaker,
      bookmakerName: match.prices?.[fav.selection]
        ?.find((r) => r.bookmaker === fav.bookmaker)?.bookmakerName ?? fav.bookmaker,
      // The book's own page for this race, where the feed published one
      // (`source_url` per book on the runner, lib/racing/board.js). Off the
      // same row the name above comes off, so it is this book's page and no
      // other's.
      ...legLink(match, fav.selection, fav.bookmaker),
      price: fav.price,
      viaBestPrice: fav.viaBestPrice,
      pFair,
      pModel: pFair,
      p: pFair,
      edge: 0,
      // With no model the EV is exactly minus the book's margin on the leg.
      // Printed rather than hidden: it is what an all-up costs.
      ev: pFair * fav.price - 1,
      books: 1,
      active: true,
    });
  }

  // SHORTEST FAVOURITE FIRST, not first to jump.
  //
  // The card is read in start order above because that is the order the races
  // arrive in, and it used to be the order the legs were TAKEN in too: the
  // next two races, whatever they happened to be paying. That made the ticket
  // a fact about the clock rather than about the runners — a $2.50 and a $2.55
  // at three o'clock, when the two standouts of the afternoon were at four and
  // half past.
  //
  // An all-up has no model and no edge to rank on (see the document above), so
  // the only thing it can honestly rank on is the price: the shortest
  // favourite on the card is the one the market is surest about. Ordering by
  // it makes the two-leg ticket the two surest things of the day, and the
  // three- and four-leg tickets those two plus the next surest — which is
  // exactly what "Low risk, Medium, High" claims about them.
  //
  // Time is the tie-break, so a card with two favourites at the same price
  // deals the same way twice, and the KEY breaks that in turn: the whole
  // strategy is deterministic and must stay so (`appendDealt` drops a second
  // identical deal, and it can only do that if there IS a second identical one).
  //
  // THE NEXT THREE HOURS FIRST, and that is the one thing ahead of the price:
  // an all-up is a bet on an afternoon, not on a day, and its legs have to be
  // races a punter is going to be awake for. Inside that window the ordering
  // is exactly what it was.
  candidates.sort((a, b) => (a.soon === b.soon ? 0 : a.soon ? -1 : 1)
    || a.price - b.price
    || String(a.kickoff).localeCompare(String(b.kickoff))
    || a.key.localeCompare(b.key));

  if (tooSoon) {
    notes.push(`${tooSoon} race${tooSoon === 1 ? '' : 's'} jumping inside ${RACING_LEAD_MS / 60_000} minutes skipped`);
  }

  if (outOfBand) {
    notes.push(`${outOfBand} favourite${outOfBand === 1 ? '' : 's'} outside $${band[0].toFixed(2)}–$${band[1].toFixed(2)}, skipped`);
  }

  const fallbacks = candidates.filter((c) => c.viaBestPrice).length;
  if (fallbacks && ctx.bookmakerName) {
    notes.push(`${ctx.bookmakerName} is not pricing ${fallbacks} of these races · best price used`);
  }

  const tickets = [];
  for (const { legs: want } of ALL_UP_DESIGNS) {
    if (want > (s.combiner?.maxLegs ?? want)) continue;
    const picked = candidates.slice(0, want);
    if (picked.length < want) continue;
    tickets.push(printAllUp(picked, { strategy: s, ctx, forDate }));
  }

  if (!tickets.length && candidates.length) {
    const n = candidates.length;
    notes.push(`only ${n} race${n === 1 ? '' : 's'} with a favourite in the band; an all-up needs ${ALL_UP_DESIGNS[0].legs}`);
  }

  return { tickets, candidates, notes };
}

/** One all-up ticket, in the shape `runStrategy` prints. */
function printAllUp(picked, { strategy, ctx, forDate }) {
  const ordered = picked
    .slice()
    .sort((a, b) => String(a.kickoff).localeCompare(String(b.kickoff)) || a.key.localeCompare(b.key));
  const combined = combineLegs(ordered);
  const pFairCombined = ordered.reduce((acc, c) => acc * c.pFair, 1);
  const stake = ctx.stake;
  // Keyed by the DESIGN — the leg count — not by a risk word. The tier is read
  // off the chance below and can be the same word on two of the three tickets,
  // which would have made two ids one id.
  const id = `${forDate}-${strategy.id}-s${ctx.seed ?? 0}-${ordered.length}leg`;
  const eventIdOf = (c) => c.providerEventId ?? c.matchKey;
  // One slip at one book where every leg is at one book, and the punter's own
  // name for it otherwise — the same sentence a single prints, for the same
  // reason: "Best price" is an instruction to go shopping, and an all-up is one
  // slip.
  const books = new Set(ordered.map((c) => c.bookmaker));
  const bookmaker = books.size === 1 ? ordered[0].bookmakerName : (ctx.bookmakerName ?? 'Best price');

  return {
    multi: {
      id,
      // The design, and then what the design turned out to be worth. Combined
      // here is the DEVIGGED chance — every leg's `p` is `bookFair`'s reading
      // of the favourite's price with the book's margin taken off — so this
      // tier is the honest one, not the price's own optimism.
      design: ordered.length,
      // Read off the DEVIGGED chance, the same input every other printer now
      // labels from. On an all-up the two are the same number — each leg's `p`
      // IS `bookFair`'s reading of the favourite — and it is written as
      // `pFairCombined` so that stays true if the leg's `p` ever changes.
      tier: tierFor(pFairCombined),
      forDate,
      status: 'proposed',
      stake,
      bookmaker,
      ...(books.size === 1 ? { bookmakerKey: ordered[0].bookmaker } : {}),
      oddsFetchedAt: new Date(ctx.oddsFetchedAt ?? ctx.asOf ?? Date.now()).toISOString(),
      combinedOdds: combined.price,
      combinedProbability: combined.p,
      potentialReturn: cents(stake * combined.price),
      strategyId: strategy.id,
      strategyVersion: strategy.version,
      label: strategy.name,
      // NO MODEL PROBABILITY, because there is no model. Written, this ticket
      // printed "Model 13.7% · market 13.7% · edge +0.0%" — a working that says
      // nothing three times, on the one strategy in the app that has no opinion
      // to show. Leaving it off is what makes `Ticket.vue` print the line
      // Classic prints instead: "Expected return 77c per $1 — the margin, over
      // two legs", which is the true cost of an all-up and is the SEPARATE
      // field from the risk word, so a Low-risk label can never read as a tip.
      fairProbability: pFairCombined,
      edge: 0,
      ev: combined.p * combined.price - 1,
      stakeRule: 'flat',
    },
    legs: ordered.map((c) => ({
      id: `${id}-${eventIdOf(c)}`,
      multiId: id,
      eventId: eventIdOf(c),
      selection: c.number ? `${c.team} (${c.number}) to win` : `${c.team} to win`,
      match: c.match,
      sportTitle: c.sportTitle,
      commenceTime: c.kickoff,
      price: c.price,
      impliedProbability: 1 / c.price,
      matchKey: c.matchKey,
      sport: c.sport,
      bookmakerKey: c.bookmaker,
      // The race's page at the book this leg is at, frozen onto the leg the way
      // `strategy.js` freezes it: an all-up is one slip at one book, so the
      // FIRST leg's link is the one the handoff opens and the rest are the
      // paste (`openTarget`, lib/bookmakers.js).
      ...(c.link ? { link: c.link, linkKind: c.linkKind } : {}),
      modelProbability: c.pModel,
      fairProbability: c.pFair,
      edge: c.edge,
      side: c.selection,
      // THE FEED'S OWN WORD FOR "IT HAS JUMPED", carried onto the leg because
      // the leg is all `ticketState` gets to read. A race that went early or
      // late is started when the card says it is, not when the clock does.
      ...(c.inplayAt ? { inplayAt: c.inplayAt } : {}),
      ...(c.marketClosedAt ? { marketClosedAt: c.marketClosedAt } : {}),
    })),
  };
}
